import type { LisProviderSource, LisStageTestCode, LisStageTestSetup } from '@/api/lisStagesApi'
import type { LisOrderingProvider } from '@/api/lisResultsApi'

/**
 * A stage test's description (OBR-4.2) and ordering provider (ORC-12 / OBR-16),
 * as Configure → LIS Stages sets them up and the order dialog fills them
 * (z8nrz7ffvz).
 *
 * The rules mirror the backend's `lis-ordering-provider.ts`, so a mistake is
 * shown beside the field instead of coming back as a 400. The backend still
 * decides. Epic matches a provider only by NPI, so every rule ends in "an NPI,
 * or no provider".
 */

/** The HL7 2.3 field limit (OBR-4) less the code, as the contract's testName. */
export const TEST_DESCRIPTION_MAX = 199

const NPI_PATTERN = /^\d{10}$/

export const PROVIDER_SOURCE_OPTIONS: { value: LisProviderSource; label: string; hint: string }[] = [
  {
    value: 'fixed',
    label: 'Fixed provider',
    hint: 'The same provider on every order of this test.',
  },
  {
    value: 'choose',
    label: 'Choose when ordering',
    hint: 'The person placing the order enters the provider. A default below is pre-filled.',
  },
]

/** A provider as typed into four boxes. */
export interface ProviderDraft {
  npi: string
  lastName: string
  firstName: string
  middleName: string
}

export const EMPTY_PROVIDER: ProviderDraft = { npi: '', lastName: '', firstName: '', middleName: '' }

/** The add / edit form for one stage test. `''` source = no provider set up. */
export interface TestSetupDraft {
  testCode: string
  description: string
  providerSource: LisProviderSource | ''
  provider: ProviderDraft
  blockIfNoProvider: boolean
  defaultSelected: boolean
}

export function emptyTestSetup(): TestSetupDraft {
  return {
    testCode: '',
    description: '',
    providerSource: '',
    provider: { ...EMPTY_PROVIDER },
    blockIfNoProvider: false,
    defaultSelected: true,
  }
}

export function testSetupFromTest(test: LisStageTestCode): TestSetupDraft {
  return {
    testCode: test.testCode,
    description: test.description ?? '',
    providerSource: test.providerSource ?? '',
    provider: providerFromTest(test),
    blockIfNoProvider: test.blockIfNoProvider ?? false,
    defaultSelected: test.defaultSelected,
  }
}

/** The provider saved on a test: the fixed one, or the default for "choose". */
export function providerFromTest(test: LisStageTestSetup): ProviderDraft {
  return {
    npi: test.providerNpi ?? '',
    lastName: test.providerLastName ?? '',
    firstName: test.providerFirstName ?? '',
    middleName: test.providerMiddleName ?? '',
  }
}

const isBlank = (p: ProviderDraft) => Object.values(p).every((v) => v.trim() === '')

/**
 * What is wrong with a provider as typed, or null. Names are optional, even
 * with an NPI; names without one are refused, because Epic cannot match them.
 */
export function providerProblem(p: ProviderDraft): string | null {
  if (isBlank(p)) return null
  const npi = p.npi.trim()
  if (!npi) return 'Enter the NPI. Epic matches the ordering provider only by NPI, so a name alone is not enough.'
  if (!NPI_PATTERN.test(npi)) return 'NPI must be exactly 10 digits.'
  return null
}

export type TestSetupErrors = Partial<Record<'testCode' | 'description' | 'provider', string>>

/** Inline messages for the add / edit form. Empty when it can be saved. */
export function testSetupErrors(draft: TestSetupDraft): TestSetupErrors {
  const errors: TestSetupErrors = {}
  const code = draft.testCode.trim()
  if (!code) errors.testCode = 'Enter the test code.'
  else if (!/^[A-Za-z0-9]{1,20}$/.test(code)) errors.testCode = 'Letters and digits only, up to 20 characters.'

  if (draft.description.trim().length > TEST_DESCRIPTION_MAX) {
    errors.description = `At most ${TEST_DESCRIPTION_MAX} characters.`
  }

  if (draft.providerSource === 'fixed' && !draft.provider.npi.trim()) {
    errors.provider = 'A fixed provider needs an NPI: it is sent on every order of this test.'
  } else {
    const problem = providerProblem(draft.provider)
    if (problem) errors.provider = problem
  }
  return errors
}

/**
 * The form as the API takes it. Blanks become null so an edit can clear a
 * field. The provider boxes are sent only when a source uses them.
 */
export function testSetupBody(draft: TestSetupDraft): LisStageTestSetup & {
  testCode: string
  defaultSelected: boolean
} {
  const text = (v: string) => v.trim() || null
  const keepProvider = draft.providerSource !== ''
  return {
    testCode: draft.testCode.trim(),
    description: text(draft.description),
    providerSource: draft.providerSource || null,
    providerNpi: keepProvider ? text(draft.provider.npi) : null,
    providerLastName: keepProvider ? text(draft.provider.lastName) : null,
    providerFirstName: keepProvider ? text(draft.provider.firstName) : null,
    providerMiddleName: keepProvider ? text(draft.provider.middleName) : null,
    blockIfNoProvider: keepProvider ? draft.blockIfNoProvider : false,
    defaultSelected: draft.defaultSelected,
  }
}

/** `LAST, FIRST MIDDLE`, or null when the provider has no name. */
export function providerName(p: Pick<LisStageTestSetup, 'providerLastName' | 'providerFirstName' | 'providerMiddleName'>): string | null {
  const given = [p.providerFirstName, p.providerMiddleName].filter(Boolean).join(' ')
  const name = [p.providerLastName, given].filter(Boolean).join(', ')
  return name || null
}

/** The provider line on a stage's test list (AC1.5). */
export function providerSummary(test: LisStageTestSetup): string {
  if (test.providerSource === 'fixed') {
    const name = providerName(test)
    return `Fixed provider: ${name ? `${name} · ` : ''}NPI ${test.providerNpi ?? '—'}`
  }
  if (test.providerSource === 'choose') {
    return test.providerNpi ? `Chosen when ordering (default NPI ${test.providerNpi})` : 'Chosen when ordering'
  }
  return 'No ordering provider'
}

/** One "choose when ordering" test's provider, as the batch order takes it. */
export interface ChosenProvider {
  testCode: string
  npi?: string
  lastName?: string
  firstName?: string
  middleName?: string
}

/** The selected tests' chosen providers, blank parts left out. */
export function chosenProvidersBody(
  selected: readonly string[],
  chooseCodes: ReadonlySet<string>,
  drafts: Readonly<Record<string, ProviderDraft>>,
): ChosenProvider[] {
  return selected
    .filter((code) => chooseCodes.has(code))
    .map((testCode) => {
      const p = drafts[testCode] ?? EMPTY_PROVIDER
      const out: ChosenProvider = { testCode }
      for (const key of ['npi', 'lastName', 'firstName', 'middleName'] as const) {
        const value = p[key].trim()
        if (value) out[key] = value
      }
      return out
    })
}

/**
 * What the order dialog says under a "choose" test with no NPI yet: the order
 * goes without a provider, or is refused, depending on the test.
 */
export function noProviderNote(blockIfNoProvider: boolean | undefined): string {
  return blockIfNoProvider
    ? 'No NPI: this test will not be ordered until a provider is entered.'
    : 'No NPI: this test will be ordered without an ordering provider.'
}

/** A saved provider (an outgoing mapping's, z8nrz7fdc0) as the four boxes show it. */
export function providerDraftOf(p: LisOrderingProvider | null | undefined): ProviderDraft {
  return {
    npi: p?.npi ?? '',
    lastName: p?.lastName ?? '',
    firstName: p?.firstName ?? '',
    middleName: p?.middleName ?? '',
  }
}

/** The boxes as the API takes them: blank parts left out, null when nothing is left (which clears it). */
export function providerDraftBody(p: ProviderDraft): LisOrderingProvider | null {
  if (isBlank(p)) return null
  const out: LisOrderingProvider = { npi: p.npi.trim() }
  for (const key of ['lastName', 'firstName', 'middleName'] as const) {
    const value = p[key].trim()
    if (value) out[key] = value
  }
  return out
}

/** Whether the boxes differ from what is saved, ignoring blanks and spaces. */
export function providerChanged(draft: ProviderDraft, saved: LisOrderingProvider | null | undefined): boolean {
  return JSON.stringify(providerDraftBody(draft)) !== JSON.stringify(providerDraftBody(providerDraftOf(saved)))
}

/** `LAST, FIRST MIDDLE · NPI 1234567893`, the OBR-16 line a confirmation shows; null for none. */
export function orderingProviderLine(p: LisOrderingProvider | null | undefined): string | null {
  if (!p?.npi) return null
  const name = providerName({ providerLastName: p.lastName, providerFirstName: p.firstName, providerMiddleName: p.middleName })
  return `${name ? `${name} · ` : ''}NPI ${p.npi}`
}
